import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Input,
    Label,
    ListBox,
    Select,
    TextField,
} from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { ImageCropUpload } from '@/components/imageCropUpload';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { CategoryParentOption } from '../categoriesMeta';
import { NO_PARENT_VALUE, type useCategoryForm } from './useCategoryForm';

type CategoryFormModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useCategoryForm>;
    isOpen: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
    parentOptions: CategoryParentOption[];
};

const CATEGORY_IMAGE_CROP_SIZE = { width: 512, height: 512 };

export const CategoryFormModalView: React.FC<CategoryFormModalViewProps> = ({
    error,
    form,
    isOpen,
    isSubmitting,
    mode,
    onClose,
    parentOptions,
}) => {
    const { t } = useTranslation('pages');
    const {
        image,
        isValid,
        name,
        onImageChange,
        onNameChange,
        onParentChange,
        onSlugChange,
        onSubmit,
        parentId,
        slug,
    } = form;

    const copy = React.useMemo(
        () => ({
            title:
                mode === 'edit'
                    ? t('categories.components.categoryTable.form.editTitle')
                    : t('categories.components.categoryTable.form.title'),
            description: t(
                'categories.components.categoryTable.form.description',
            ),
            imageLabel: t(
                'categories.components.categoryTable.form.imageLabel',
            ),
            imageDescription: t(
                'categories.components.categoryTable.form.imageDescription',
            ),
            imageSelect: t(
                'categories.components.categoryTable.form.imageSelect',
            ),
            imageChange: t(
                'categories.components.categoryTable.form.imageChange',
            ),
            imageRemove: t(
                'categories.components.categoryTable.form.imageRemove',
            ),
            imageApply: t(
                'categories.components.categoryTable.form.imageApply',
            ),
            imageUploadLabel: t(
                'categories.components.categoryTable.form.imageUploadLabel',
            ),
            imageInvalidType: t(
                'categories.components.categoryTable.form.imageInvalidType',
            ),
            imageInvalidSize: t(
                'categories.components.categoryTable.form.imageInvalidSize',
            ),
            imageCropError: t(
                'categories.components.categoryTable.form.imageCropError',
            ),
            imageReadError: t(
                'categories.components.categoryTable.form.imageReadError',
            ),
            nameLabel: t('categories.components.categoryTable.form.nameLabel'),
            namePlaceholder: t(
                'categories.components.categoryTable.form.namePlaceholder',
            ),
            slugLabel: t('categories.components.categoryTable.form.slugLabel'),
            slugPlaceholder: t(
                'categories.components.categoryTable.form.slugPlaceholder',
            ),
            parentLabel: t(
                'categories.components.categoryTable.form.parentLabel',
            ),
            parentPlaceholder: t(
                'categories.components.categoryTable.form.parentPlaceholder',
            ),
            parentNone: t(
                'categories.components.categoryTable.form.parentNone',
            ),
            cancel: t('categories.components.categoryTable.form.cancel'),
            submit:
                mode === 'edit'
                    ? t('categories.components.categoryTable.form.editSubmit')
                    : t('categories.components.categoryTable.form.submit'),
        }),
        [mode, t],
    );

    return (
        <FormDrawer
            footer={
                <>
                    <Button
                        isDisabled={isSubmitting}
                        onPress={onClose}
                        variant="tertiary"
                        {...getNodeTestIdProp('category-form-cancel')}
                    >
                        {copy.cancel}
                    </Button>
                    <Button
                        isDisabled={!isValid}
                        isPending={isSubmitting}
                        onPress={onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('category-form-submit')}
                    >
                        {copy.submit}
                    </Button>
                </>
            }
            isOpen={isOpen}
            onClose={onClose}
            subtitle={copy.description}
            testId="category-form"
            title={copy.title}
        >
            <div
                className="flex flex-col gap-1.5"
                {...getNodeTestIdProp('category-form-image-field')}
            >
                <Label
                    className="text-sm font-medium text-foreground"
                    {...getNodeTestIdProp('category-form-image-label')}
                >
                    {copy.imageLabel}
                </Label>
                <ImageCropUpload
                    applyLabel={copy.imageApply}
                    aspectClassName="aspect-square"
                    boxClassName="mx-auto w-[150px]"
                    changeLabel={copy.imageChange}
                    cropErrorMessage={copy.imageCropError}
                    cropSize={CATEGORY_IMAGE_CROP_SIZE}
                    description={copy.imageDescription}
                    id="category-form-image"
                    invalidSizeMessage={copy.imageInvalidSize}
                    invalidTypeMessage={copy.imageInvalidType}
                    label={copy.imageUploadLabel}
                    onChange={onImageChange}
                    readErrorMessage={copy.imageReadError}
                    removeLabel={copy.imageRemove}
                    selectLabel={copy.imageSelect}
                    value={image}
                />
            </div>
            <TextField
                isRequired
                onChange={onNameChange}
                value={name}
                {...getNodeTestIdProp('category-form-name')}
            >
                <Label>{copy.nameLabel}</Label>
                <Input placeholder={copy.namePlaceholder} />
            </TextField>
            <TextField
                isRequired
                onChange={onSlugChange}
                value={slug}
                {...getNodeTestIdProp('category-form-slug')}
            >
                <Label>{copy.slugLabel}</Label>
                <Input placeholder={copy.slugPlaceholder} />
            </TextField>
            <Select
                fullWidth
                onChange={(value) =>
                    onParentChange(
                        value === NO_PARENT_VALUE ? null : (value as string),
                    )
                }
                placeholder={copy.parentPlaceholder}
                value={parentId ?? NO_PARENT_VALUE}
                variant="secondary"
            >
                <Label>{copy.parentLabel}</Label>
                <Select.Trigger {...getNodeTestIdProp('category-form-parent')}>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        <ListBox.Item
                            id={NO_PARENT_VALUE}
                            textValue={copy.parentNone}
                        >
                            {copy.parentNone}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {parentOptions.map((option) => (
                            <ListBox.Item
                                id={option.id}
                                key={option.id}
                                textValue={option.name}
                            >
                                <span
                                    style={{
                                        paddingInlineStart: `${option.depth * 12}px`,
                                    }}
                                >
                                    {option.name}
                                </span>
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>
            {error ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('category-form-error')}
                >
                    {error}
                </p>
            ) : null}
        </FormDrawer>
    );
};
